import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  GU_ASSORTMENT_TAG_LABELS,
  GU_CODE_TYPE_LABELS,
  GU_DELIVERY_LABELS,
  GU_ORDER_STATUS_LABELS,
  GU_ORDER_STATUSES,
  type GuCardImportResultDto,
  type GuClosingEventDto,
  type GuClosingRunDto,
  type GuStockRowDto,
  type GuCardPoolDto,
  type GuOrderDto,
  type GuShopDetailDto,
  type GuStaffShopDto,
  type GuOrderStatus,
  GU_ASSORTMENT_TAGS,
  GU_OCCASION_LABELS,
  GU_OCCASIONS,
  type GuAssortmentDetailDto,
  type GuAssortmentDto,
  type GuBuyerDto,
  type GuFeesDto,
  type GuOccasion,
  type GuSeasonDto,
  type GuShippingDto,
  type ProductListItemDto,
  type Settings,
} from '@thebb/shared';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';
import { FinanceCustomerPicker } from '../components/FinanceCustomerPicker';
import { MoneyInput, kr } from './Catalogue';

type Tab = 'seasons' | 'assortments' | 'buyers' | 'orders' | 'cards' | 'shops' | 'closing';

/** Admin → Gaveuniverset (phase 3, P3.1): seasons with fees and shipping, assortments, buyers. */
export function GaveuniversetPage() {
  const [tab, setTab] = useState<Tab>('orders');
  const tabs: [Tab, string][] = [['orders', 'Ordrer'], ['shops', 'Gaveshops'], ['closing', 'Lukning og lager'], ['cards', 'Gavekort'], ['seasons', 'Sæsoner og gebyrer'], ['assortments', 'Sortimenter'], ['buyers', 'Købere']];
  return (
    <div className="space-y-4">
      <PageTitle title="Gaveuniverset" subtitle="Købernes ordrer, sæsoner med gebyrer og levering, sortimenter og købere. Købersitet ligger på /gu." />
      <div className="flex gap-1 border-b border-line">
        {tabs.map(([k, label]) => (
          <button key={k} className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === k ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      {tab === 'seasons' && <SeasonsTab />}
      {tab === 'assortments' && <AssortmentsTab />}
      {tab === 'buyers' && <BuyersTab />}
      {tab === 'orders' && <OrdersTab />}
      {tab === 'cards' && <CardsTab />}
      {tab === 'shops' && <ShopsTab />}
      {tab === 'closing' && <ClosingTab />}
    </div>
  );
}

// --- seasons ---------------------------------------------------------------------------------------

type SeasonForm = { id: string | null; occasion: GuOccasion; year: number; name: string; salesFrom: string; salesTo: string; opensFrom: string; closesBy: string; catalogueUrl: string; active: boolean; fees: GuFeesDto | null; shipping: GuShippingDto | null };

function SeasonsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<SeasonForm | null>(null);
  const list = useQuery({ queryKey: ['gu-seasons'], queryFn: () => get<GuSeasonDto[]>('/gaveuniverset/seasons') });
  const open = (s?: GuSeasonDto) =>
    setForm(
      s
        ? { id: s.id, occasion: s.occasion, year: s.year, name: s.name, salesFrom: s.salesFrom ?? '', salesTo: s.salesTo ?? '', opensFrom: s.opensFrom ?? '', closesBy: s.closesBy ?? '', catalogueUrl: s.catalogueUrl ?? '', active: s.active, fees: s.fees, shipping: s.shipping }
        : { id: null, occasion: 'christmas', year: new Date().getFullYear() + 1, name: `Jul ${new Date().getFullYear() + 1}`, salesFrom: '', salesTo: '', opensFrom: '', closesBy: '', catalogueUrl: '', active: true, fees: null, shipping: null },
    );
  // A new season starts from Gaveuniverset's known values for its occasion.
  const defaults = useQuery({ queryKey: ['gu-defaults', form?.occasion], queryFn: () => get<{ fees: GuFeesDto; shipping: GuShippingDto }>(`/gaveuniverset/season-defaults/${form!.occasion}`), enabled: !!form && !form.id });
  useEffect(() => {
    if (form && !form.id && defaults.data) setForm((f) => (f ? { ...f, fees: defaults.data.fees, shipping: defaults.data.shipping } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.data]);
  const save = useMutation({
    mutationFn: (f: SeasonForm) => {
      const body = { occasion: f.occasion, year: f.year, name: f.name, salesFrom: f.salesFrom || null, salesTo: f.salesTo || null, opensFrom: f.opensFrom || null, closesBy: f.closesBy || null, catalogueUrl: f.catalogueUrl || null, active: f.active, fees: f.fees ?? undefined, shipping: f.shipping ?? undefined };
      return f.id ? put(`/gaveuniverset/seasons/${f.id}`, body) : post('/gaveuniverset/seasons', body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gu-seasons'] }); setForm(null); setError(null); },
    onError: setError,
  });
  const editable = can('gu.manage');
  return (
    <div className="space-y-3">
      <ErrorBox error={error} />
      {editable && <button className="btn btn-primary" onClick={() => open()}>Ny sæson</button>}
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0 text-sm">
          <thead><tr><th>Sæson</th><th>Anledning</th><th>Salg</th><th>Shops åbner / lukker</th><th className="text-right">Fakturagebyr</th><th className="text-right">Fysiske kort</th><th>Erhvervslevering</th><th /></tr></thead>
          <tbody>
            {(list.data ?? []).map((s) => (
              <tr key={s.id}>
                <td className="font-medium">{s.name} {!s.active && <Badge>inaktiv</Badge>}</td>
                <td>{GU_OCCASION_LABELS[s.occasion]}</td>
                <td className="whitespace-nowrap">{s.salesFrom ?? '–'} – {s.salesTo ?? '–'}</td>
                <td className="whitespace-nowrap">{s.opensFrom ?? '–'} / {s.closesBy ?? '–'}</td>
                <td className="text-right tabular-nums">{kr(s.fees.invoiceFeeMinor)}</td>
                <td className="text-right tabular-nums">{kr(s.fees.giftCardFreightMinor)}</td>
                <td>{s.shipping.businessFixedPerCodeMinor != null ? `${kr(s.shipping.businessFixedPerCodeMinor)} pr. kode` : s.shipping.businessBands.length ? `${s.shipping.businessBands.length} intervaller` : `${kr(s.shipping.businessPerCodeMinor)} pr. kode`}</td>
                <td className="text-right">{editable && <button className="btn py-1 text-xs" onClick={() => open(s)}>Rediger</button>}</td>
              </tr>
            ))}
            {list.data?.length === 0 && <tr><td colSpan={8} className="text-muted">Ingen sæsoner endnu. Opret den første: gebyrer og fragt starter med Gaveuniversets kendte værdier.</td></tr>}
          </tbody>
        </table>
      )}
      <p className="text-xs text-muted">Alle beløb er i kr. ekskl. moms. Startværdierne er Gaveuniversets kendte værdier fra koden; de rigtige står i Gaveuniversets database og skal tastes her.</p>
      {form && <SeasonModal form={form} setForm={setForm} saving={save.isPending} onSave={() => save.mutate(form)} />}
    </div>
  );
}

function SeasonModal({ form, setForm, saving, onSave }: { form: SeasonForm; setForm: (f: SeasonForm | null) => void; saving: boolean; onSave: () => void }) {
  const set = (p: Partial<SeasonForm>) => setForm({ ...form, ...p });
  const fee = (k: keyof GuFeesDto, label: string) => (
    <label className="block"><span className="label">{label}</span><MoneyInput className="input text-right" value={form.fees?.[k] as number} onChange={(v) => form.fees && set({ fees: { ...form.fees, [k]: v ?? 0 } })} /></label>
  );
  const ship = (k: 'parcelShopPerCodeMinor' | 'homePerCodeMinor' | 'businessPerCodeMinor', label: string) => (
    <label className="block"><span className="label">{label}</span><MoneyInput className="input text-right" value={form.shipping?.[k]} onChange={(v) => form.shipping && set({ shipping: { ...form.shipping, [k]: v ?? 0 } })} /></label>
  );
  const bands = form.shipping?.businessBands ?? [];
  const setBands = (b: GuShippingDto['businessBands']) => form.shipping && set({ shipping: { ...form.shipping, businessBands: b } });
  return (
    <Modal title={form.id ? `Rediger ${form.name}` : 'Ny sæson'} onClose={() => setForm(null)}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-3 gap-2">
          <label className="block"><span className="label">Anledning</span>
            <select className="input" value={form.occasion} disabled={!!form.id} onChange={(e) => set({ occasion: e.target.value as GuOccasion })}>{GU_OCCASIONS.map((o) => <option key={o} value={o}>{GU_OCCASION_LABELS[o]}</option>)}</select>
          </label>
          <label className="block"><span className="label">År</span><input className="input" type="number" value={form.year} onChange={(e) => set({ year: Number(e.target.value) })} /></label>
          <label className="block"><span className="label">Navn</span><input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} /></label>
          <label className="block"><span className="label">Salg fra</span><input className="input" type="date" value={form.salesFrom} onChange={(e) => set({ salesFrom: e.target.value })} /></label>
          <label className="block"><span className="label">Salg til</span><input className="input" type="date" value={form.salesTo} onChange={(e) => set({ salesTo: e.target.value })} /></label>
          <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} /> Aktiv</label>
          <label className="block"><span className="label">Shops åbner tidligst</span><input className="input" type="date" value={form.opensFrom} onChange={(e) => set({ opensFrom: e.target.value })} /></label>
          <label className="block"><span className="label">Shops lukker senest</span><input className="input" type="date" value={form.closesBy} onChange={(e) => set({ closesBy: e.target.value })} /></label>
          <label className="block"><span className="label">Katalog (iPaper)</span><input className="input" value={form.catalogueUrl} placeholder="https://" onChange={(e) => set({ catalogueUrl: e.target.value })} /></label>
        </div>
        {!form.fees || !form.shipping ? <Spinner /> : (
          <>
            <div>
              <div className="label">Gebyrer (kr. ekskl. moms)</div>
              <div className="grid grid-cols-3 gap-2">
                {fee('invoiceFeeMinor', 'Fakturagebyr')}
                {fee('giftCardFreightMinor', 'Fragt, fysiske gavekort')}
                {fee('carryingSupportMinor', 'Opbæring')}
                {fee('extraAddressFeeMinor', 'Pr. ekstra leveringsadresse')}
                <label className="block"><span className="label">Gratis ekstra adresser</span><input className="input" type="number" min={0} value={form.fees.freeExtraAddresses} onChange={(e) => set({ fees: { ...form.fees!, freeExtraAddresses: Math.max(0, Number(e.target.value) || 0) } })} /></label>
                {fee('minimumRevenueMinor', 'Mindste omsætning, første ordre')}
                {fee('invoiceFromMinor', 'Faktura fra (inkl. moms; 0 = altid)')}
              </div>
            </div>
            <div>
              <div className="label">Ændring af antal koder</div>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.fees.adjustUp} onChange={(e) => set({ fees: { ...form.fees!, adjustUp: e.target.checked } })} /> Køb flere</label>
                {fee('adjustUpFeeMinor', 'Gebyr pr. ekstra kode')}
                <span />
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.fees.adjustDown} onChange={(e) => set({ fees: { ...form.fees!, adjustDown: e.target.checked } })} /> Giv koder tilbage</label>
                {fee('adjustDownFeeMinor', 'Gebyr pr. kode tilbage')}
                <label className="block"><span className="label">Kun med flere koder end</span><input className="input" type="number" min={0} value={form.fees.minCodesForAdjust} onChange={(e) => set({ fees: { ...form.fees!, minCodesForAdjust: Math.max(0, Number(e.target.value) || 0) } })} /></label>
                <label className="block"><span className="label">Advarsel ved flere gaver end</span><input className="input" type="number" min={0} value={form.fees.giftCountWarning} onChange={(e) => set({ fees: { ...form.fees!, giftCountWarning: Math.max(0, Number(e.target.value) || 0) } })} /></label>
                <label className="col-span-2 block"><span className="label">Advarselstekst</span><input className="input" value={form.fees.giftCountWarningText} onChange={(e) => set({ fees: { ...form.fees!, giftCountWarningText: e.target.value } })} /></label>
              </div>
            </div>
            <div>
              <div className="label">Fragt (kr. ekskl. moms)</div>
              <div className="grid grid-cols-3 gap-2">
                {ship('parcelShopPerCodeMinor', 'Pakkeshop, pr. kode')}
                {ship('homePerCodeMinor', 'Privat, pr. kode')}
                {ship('businessPerCodeMinor', 'Erhverv, pr. kode (uden intervaller)')}
                <label className="block"><span className="label">Erhverv, fast pris pr. kode</span><MoneyInput className="input text-right" value={form.shipping.businessFixedPerCodeMinor} onChange={(v) => set({ shipping: { ...form.shipping!, businessFixedPerCodeMinor: v } })} /></label>
              </div>
              <div className="mt-2 text-xs text-muted">Erhvervslevering efter antal koder; genbestillinger betaler forskellen mellem intervallerne. Uden intervaller og uden fast pris gælder prisen pr. kode.</div>
              <table className="table mt-1 text-sm">
                <thead><tr><th>Fra koder</th><th>Til koder</th><th className="text-right">Pris</th><th /></tr></thead>
                <tbody>
                  {bands.map((b, i) => (
                    <tr key={i}>
                      <td><input className="input w-24" type="number" min={0} value={b.from} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, from: Number(e.target.value) || 0 } : x)))} /></td>
                      <td><input className="input w-24" type="number" min={0} placeholder="og op" value={b.to ?? ''} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, to: e.target.value === '' ? null : Number(e.target.value) } : x)))} /></td>
                      <td className="text-right"><MoneyInput value={b.priceMinor} onChange={(v) => setBands(bands.map((x, j) => (j === i ? { ...x, priceMinor: v ?? 0 } : x)))} /></td>
                      <td><button className="text-muted" onClick={() => setBands(bands.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn mt-1" onClick={() => { const last = bands[bands.length - 1]; setBands([...bands, { from: last ? (last.to ?? last.from) + 1 : 0, to: null, priceMinor: last?.priceMinor ?? 0 }]); }}>+ interval</button>
            </div>
            <div>
              <div className="label">Levering, som køberne kan vælge</div>
              <div className="grid grid-cols-3 gap-2">
                <label className="block"><span className="label">Første leveringsdato</span><input className="input" type="date" value={form.shipping.deliveryFrom ?? ''} onChange={(e) => set({ shipping: { ...form.shipping!, deliveryFrom: e.target.value || null } })} /></label>
                <label className="block"><span className="label">Sidste leveringsdato</span><input className="input" type="date" value={form.shipping.deliveryTo ?? ''} onChange={(e) => set({ shipping: { ...form.shipping!, deliveryTo: e.target.value || null } })} /></label>
                <label className="block"><span className="label">Tidligst dage efter bestilling</span><input className="input" type="number" min={0} value={form.shipping.minLeadDays} onChange={(e) => set({ shipping: { ...form.shipping!, minLeadDays: Math.max(0, Number(e.target.value) || 0) } })} /></label>
                <label className="block"><span className="label">Shop lukker dage før levering</span><input className="input" type="number" min={0} value={form.shipping.closeDaysBeforeDelivery} onChange={(e) => set({ shipping: { ...form.shipping!, closeDaysBeforeDelivery: Math.max(0, Number(e.target.value) || 0) } })} /></label>
                <div className="col-span-2">
                  <span className="label">Leveringsdage</span>
                  <div className="flex flex-wrap gap-3 pt-1">
                    {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map((d, i) => {
                      const days = form.shipping!.deliveryWeekdays;
                      const on = days.includes(i + 1);
                      return <label key={d} className="flex items-center gap-1"><input type="checkbox" checked={on} onChange={() => { const next = on ? days.filter((x) => x !== i + 1) : [...days, i + 1].sort(); if (next.length) set({ shipping: { ...form.shipping!, deliveryWeekdays: next } }); }} /> {d}</label>;
                    })}
                  </div>
                </div>
              </div>
              <div className="mt-1 text-xs text-muted">Tom dato = ingen grænse. Den gamle checkout tillod kun fredage, tidligst 14 dage frem, og lukning 14 dage før levering.</div>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={() => setForm(null)}>Annuller</button>
          <button className="btn btn-primary" disabled={saving || !form.fees} onClick={onSave}>Gem</button>
        </div>
      </div>
    </Modal>
  );
}

// --- assortments -----------------------------------------------------------------------------------

type AssortmentForm = { id: string | null; name: string; occasion: GuOccasion; giftPriceMinor: number | null; maxRetailMinor: number | null; budgetTitle: string; favouriteTitle: string; tags: string[]; published: boolean; position: number; products: GuAssortmentDetailDto['products'] };

function AssortmentsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [occasion, setOccasion] = useState<GuOccasion | ''>('');
  const [form, setForm] = useState<AssortmentForm | null>(null);
  const list = useQuery({ queryKey: ['gu-assortments', occasion], queryFn: () => get<GuAssortmentDto[]>(`/gaveuniverset/assortments${occasion ? `?occasion=${occasion}` : ''}`) });
  const editable = can('gu.manage');
  const open = async (id?: string) => {
    setError(null);
    if (!id) return setForm({ id: null, name: '', occasion: occasion || 'christmas', giftPriceMinor: null, maxRetailMinor: null, budgetTitle: '', favouriteTitle: '', tags: ['budget'], published: false, position: 100, products: [] });
    try {
      const a = await get<GuAssortmentDetailDto>(`/gaveuniverset/assortments/${id}`);
      setForm({ id: a.id, name: a.name, occasion: a.occasion, giftPriceMinor: a.giftPriceMinor, maxRetailMinor: a.maxRetailMinor, budgetTitle: a.budgetTitle ?? '', favouriteTitle: a.favouriteTitle ?? '', tags: a.tags, published: a.published, position: a.position, products: a.products });
    } catch (e) {
      setError(e);
    }
  };
  const save = useMutation({
    mutationFn: (f: AssortmentForm) => {
      const body = { name: f.name, occasion: f.occasion, giftPriceMinor: f.giftPriceMinor ?? 0, maxRetailMinor: f.maxRetailMinor, budgetTitle: f.budgetTitle || null, favouriteTitle: f.favouriteTitle || null, tags: f.tags, published: f.published, position: f.position, products: f.products.map((p) => ({ productId: p.productId, prepared: p.prepared })) };
      return f.id ? put(`/gaveuniverset/assortments/${f.id}`, body) : post('/gaveuniverset/assortments', body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gu-assortments'] }); setForm(null); },
    onError: setError,
  });
  const remove = useMutation({ mutationFn: (id: string) => del(`/gaveuniverset/assortments/${id}`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gu-assortments'] }); setForm(null); }, onError: setError });
  const [imported, setImported] = useState<{ assortments: number; created: number; updated: number; products: number; missingProducts: number } | null>(null);
  const importX3 = useMutation({ mutationFn: () => post<NonNullable<typeof imported>>('/gaveuniverset/assortments/import-x3'), onSuccess: (r) => { setImported(r); void qc.invalidateQueries({ queryKey: ['gu-assortments'] }); }, onError: setError });
  return (
    <div className="space-y-3">
      <ErrorBox error={error} />
      <div className="flex items-center gap-2">
        <select className="input w-44" value={occasion} onChange={(e) => setOccasion(e.target.value as GuOccasion | '')}>
          <option value="">Alle anledninger</option>
          {GU_OCCASIONS.map((o) => <option key={o} value={o}>{GU_OCCASION_LABELS[o]}</option>)}
        </select>
        {editable && <button className="btn btn-primary" onClick={() => void open()}>Nyt sortiment</button>}
        {editable && <button className="btn" disabled={importX3.isPending} title="Henter sortimenterne fra 3x; matcher på 3x' id, så det kan køres igen" onClick={() => importX3.mutate()}>{importX3.isPending ? 'Henter…' : 'Hent fra 3x'}</button>}
      </div>
      {imported && (
        <div className="rounded-md border border-line bg-white p-3 text-sm">
          {imported.assortments} sortimenter fra 3x: {imported.created} nye, {imported.updated} opdateret, {imported.products} produkter koblet
          {imported.missingProducts > 0 && <span className="text-amber-700">; {imported.missingProducts} produkter findes ikke her endnu (de kommer med, når shoppen eller produktet flyttes, og en ny hentning kobler dem)</span>}.
          <span className="text-muted"> Gavepriserne er overtaget fra 3x i kr. ekskl. moms.</span>
          <button className="btn ml-3 py-0.5 text-xs" onClick={() => setImported(null)}>Luk</button>
        </div>
      )}
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0 text-sm">
          <thead><tr><th>Sortiment</th><th>Anledning</th><th className="text-right">Gavepris</th><th className="text-right">Produkter</th><th className="text-right">Favoritter</th><th>Mærker</th><th>Status</th></tr></thead>
          <tbody>
            {(list.data ?? []).map((a) => (
              <tr key={a.id} className="cursor-pointer hover:bg-canvas" onClick={() => void open(a.id)}>
                <td className="font-medium">{a.name}{a.budgetTitle && <div className="text-xs text-muted">{a.budgetTitle}</div>}</td>
                <td>{GU_OCCASION_LABELS[a.occasion]}</td>
                <td className="text-right tabular-nums">{kr(a.giftPriceMinor)}</td>
                <td className="text-right tabular-nums">{a.productCount}</td>
                <td className="text-right tabular-nums">{a.favouriteCount}</td>
                <td>{a.tags.map((t) => <Badge key={t}>{GU_ASSORTMENT_TAG_LABELS[t as keyof typeof GU_ASSORTMENT_TAG_LABELS] ?? t}</Badge>)}</td>
                <td>{a.published ? <Badge tone="green">Udgivet</Badge> : <Badge>Kladde</Badge>}</td>
              </tr>
            ))}
            {list.data?.length === 0 && <tr><td colSpan={7} className="text-muted">Ingen sortimenter endnu. Et sortiment er et budget (fx 300 kr.) med de produkter, købere kan vælge imellem.</td></tr>}
          </tbody>
        </table>
      )}
      {form && <AssortmentModal form={form} setForm={setForm} editable={editable} saving={save.isPending} onSave={() => save.mutate(form)} onDelete={form.id ? () => confirm(`Slet ${form.name}?`) && remove.mutate(form.id!) : undefined} />}
    </div>
  );
}

function AssortmentModal({ form, setForm, editable, saving, onSave, onDelete }: { form: AssortmentForm; setForm: (f: AssortmentForm | null) => void; editable: boolean; saving: boolean; onSave: () => void; onDelete?: () => void }) {
  const set = (p: Partial<AssortmentForm>) => setForm({ ...form, ...p });
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const found = useQuery({ queryKey: ['gu-product-search', debounced], queryFn: () => get<{ items: ProductListItemDto[] }>(`/catalogue/products?q=${encodeURIComponent(debounced)}&perPage=20`), enabled: debounced.length >= 2 });
  const inList = new Set(form.products.map((p) => p.productId));
  return (
    <Modal title={form.id ? form.name : 'Nyt sortiment'} onClose={() => setForm(null)}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-3 gap-2">
          <label className="col-span-2 block"><span className="label">Navn</span><input className="input" value={form.name} disabled={!editable} onChange={(e) => set({ name: e.target.value })} /></label>
          <label className="block"><span className="label">Anledning</span><select className="input" value={form.occasion} disabled={!editable} onChange={(e) => set({ occasion: e.target.value as GuOccasion })}>{GU_OCCASIONS.map((o) => <option key={o} value={o}>{GU_OCCASION_LABELS[o]}</option>)}</select></label>
          <label className="block"><span className="label">Gavepris (kr. ekskl. moms)</span><MoneyInput className="input text-right" value={form.giftPriceMinor} disabled={!editable} onChange={(giftPriceMinor) => set({ giftPriceMinor })} /></label>
          <label className="block"><span className="label">Vejl. udsalgspris op til</span><MoneyInput className="input text-right" value={form.maxRetailMinor} disabled={!editable} onChange={(maxRetailMinor) => set({ maxRetailMinor })} /></label>
          <label className="block"><span className="label">Rækkefølge</span><input className="input" type="number" value={form.position} disabled={!editable} onChange={(e) => set({ position: Number(e.target.value) || 0 })} /></label>
          <label className="block"><span className="label">Budgettitel</span><input className="input" value={form.budgetTitle} disabled={!editable} placeholder="fx Gaver til 300 kr." onChange={(e) => set({ budgetTitle: e.target.value })} /></label>
          <label className="block"><span className="label">Favorittitel</span><input className="input" value={form.favouriteTitle} disabled={!editable} placeholder="fx Vores favoritter" onChange={(e) => set({ favouriteTitle: e.target.value })} /></label>
          <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={form.published} disabled={!editable} onChange={(e) => set({ published: e.target.checked })} /> Udgivet (vises for købere)</label>
        </div>
        <div className="flex flex-wrap gap-3">
          {GU_ASSORTMENT_TAGS.map((t) => (
            <label key={t} className="flex items-center gap-1"><input type="checkbox" checked={form.tags.includes(t)} disabled={!editable} onChange={(e) => set({ tags: e.target.checked ? [...form.tags, t] : form.tags.filter((x) => x !== t) })} /> {GU_ASSORTMENT_TAG_LABELS[t]}</label>
          ))}
        </div>
        <div>
          <div className="label">Produkter ({form.products.length}, heraf {form.products.filter((p) => p.prepared).length} favoritter)</div>
          {editable && (
            <div className="relative mb-2">
              <input className="input" placeholder="Søg produkt at tilføje (navn eller varenr.)" value={q} onChange={(e) => setQ(e.target.value)} />
              {debounced.length >= 2 && found.data && (
                <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-line bg-white shadow-lg">
                  {found.data.items.filter((p) => !inList.has(p.id)).map((p) => (
                    <button key={p.id} type="button" className="block w-full px-3 py-2 text-left hover:bg-canvas" onClick={() => { set({ products: [...form.products, { productId: p.id, title: p.title, sku: p.number, imageUrl: p.image?.thumbUrl ?? p.imageUrl, prepared: false, position: (form.products.length + 1) * 10 }] }); setQ(''); }}>
                      {p.title} <span className="text-muted">· {p.number ?? ''}{p.vendorName ? ` · ${p.vendorName}` : ''}</span>
                    </button>
                  ))}
                  {found.data.items.length === 0 && <div className="px-3 py-2 text-muted">Ingen produkter matcher</div>}
                </div>
              )}
            </div>
          )}
          <div className="max-h-80 overflow-y-auto rounded-md border border-line">
            {form.products.map((p, i) => (
              <div key={p.productId} className="flex items-center gap-3 border-b border-line px-3 py-1.5 last:border-0">
                <div className="h-8 w-8 shrink-0 rounded bg-canvas">{p.imageUrl && <img src={p.imageUrl} alt="" className="h-full w-full object-contain" />}</div>
                <div className="min-w-0 flex-1 truncate">{p.title} <span className="text-muted">{p.sku}</span></div>
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={p.prepared} disabled={!editable} onChange={(e) => set({ products: form.products.map((x, j) => (j === i ? { ...x, prepared: e.target.checked } : x)) })} /> Favorit</label>
                {editable && <button className="text-muted" onClick={() => set({ products: form.products.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
            {form.products.length === 0 && <div className="px-3 py-2 text-muted">Ingen produkter endnu.</div>}
          </div>
        </div>
        {editable && (
          <div className="flex justify-between gap-2">
            {onDelete ? <button className="btn text-danger" onClick={onDelete}>Slet</button> : <span />}
            <div className="flex gap-2">
              <button className="btn" onClick={() => setForm(null)}>Annuller</button>
              <button className="btn btn-primary" disabled={saving || !form.name.trim() || form.giftPriceMinor == null} onClick={onSave}>Gem</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// --- buyers ----------------------------------------------------------------------------------------

type BuyerForm = Omit<GuBuyerDto, 'id' | 'hasPassword' | 'lastLoginAt' | 'createdAt'> & { id: string | null };

function BuyersTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [q, setQ] = useState('');
  const [form, setForm] = useState<BuyerForm | null>(null);
  const list = useQuery({ queryKey: ['gu-buyers', q], queryFn: () => get<{ items: GuBuyerDto[]; total: number }>(`/gaveuniverset/buyers?perPage=100${q ? `&q=${encodeURIComponent(q)}` : ''}`) });
  const save = useMutation({
    mutationFn: ({ id, ...body }: BuyerForm) => (id ? put(`/gaveuniverset/buyers/${id}`, body) : post('/gaveuniverset/buyers', body)),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gu-buyers'] }); setForm(null); setError(null); },
    onError: setError,
  });
  const editable = can('gu.buyers');
  const loginAs = useMutation({ mutationFn: (id: string) => post<{ url: string }>(`/gaveuniverset/buyers/${id}/login-link`), onSuccess: (r) => window.open(r.url, '_blank', 'noopener'), onError: setError });
  const empty: BuyerForm = { id: null, companyName: '', contactName: '', email: '', phone: null, cvr: null, ean: null, street: null, zip: null, city: null, countryCode: 'DK', status: 'active', marketingConsent: false, financeCustomerNumber: null };
  const txt = (k: keyof BuyerForm, label: string, cls = '') =>
    form && <label className={`block ${cls}`}><span className="label">{label}</span><input className="input" value={(form[k] as string | null) ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value || null })} /></label>;
  return (
    <div className="space-y-3">
      <ErrorBox error={error} />
      <div className="flex items-center gap-2">
        <input className="input w-72" placeholder="Søg firma, kontakt, e-mail eller CVR" value={q} onChange={(e) => setQ(e.target.value)} />
        {editable && <button className="btn btn-primary" onClick={() => setForm(empty)}>Ny køber</button>}
        <span className="text-sm text-muted">{list.data?.total ?? 0} købere</span>
      </div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0 text-sm">
          <thead><tr><th>Firma</th><th>Kontakt</th><th>E-mail</th><th>CVR</th><th>Faktureres til</th><th>Sidst logget ind</th><th>Status</th></tr></thead>
          <tbody>
            {(list.data?.items ?? []).map((b) => (
              <tr key={b.id} className={editable ? 'cursor-pointer hover:bg-canvas' : ''} onClick={() => editable && setForm({ ...b })}>
                <td className="font-medium">{b.companyName}</td>
                <td>{b.contactName}</td>
                <td>{b.email}</td>
                <td>{b.cvr ?? '–'}</td>
                <td>{b.financeCustomerNumber ?? '–'}</td>
                <td>{b.lastLoginAt ? fmtTime(b.lastLoginAt) : '–'}</td>
                <td>{b.status === 'active' ? <Badge tone="green">Aktiv</Badge> : <Badge>Spærret</Badge>}{!b.hasPassword && <Badge tone="amber">Ingen adgangskode</Badge>}</td>
              </tr>
            ))}
            {list.data?.items.length === 0 && <tr><td colSpan={7} className="text-muted">Ingen købere. De opretter sig selv på købersitet (P3.2) eller flyttes fra Gaveuniverset (P3.7).</td></tr>}
          </tbody>
        </table>
      )}
      {form && (
        <Modal title={form.id ? form.companyName : 'Ny køber'} onClose={() => setForm(null)}>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {txt('companyName', 'Firma')}
              {txt('contactName', 'Kontaktperson')}
              {txt('email', 'E-mail')}
              {txt('phone', 'Telefon')}
              {txt('cvr', 'CVR (8 cifre)')}
              {txt('ean', 'EAN (13 cifre)')}
              {txt('street', 'Adresse', 'col-span-2')}
              {txt('zip', 'Postnr.')}
              {txt('city', 'By')}
            </div>
            <FinanceCustomerPicker value={form.financeCustomerNumber} onChange={(financeCustomerNumber) => setForm({ ...form, financeCustomerNumber })} />
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.status === 'active'} onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'disabled' })} /> Aktiv</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.marketingConsent} onChange={(e) => setForm({ ...form, marketingConsent: e.target.checked })} /> Samtykke til markedsføring</label>
            </div>
            {form.id && <BuyerShops buyerId={form.id} />}
            <div className="flex justify-between gap-2">
              {form.id ? (
                <button className="btn" disabled={loginAs.isPending} title="Åbner købersitet som køberen i en time; det står i loggen" onClick={() => loginAs.mutate(form.id!)}>Log ind som køber</button>
              ) : <span />}
              <div className="flex gap-2">
                <button className="btn" onClick={() => setForm(null)}>Annuller</button>
                <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate(form)}>Gem</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- orders (P3.3) -------------------------------------------------------------------------------------

const statusTone: Record<GuOrderStatus, 'amber' | 'green' | 'red' | 'blue'> = { awaiting_payment: 'amber', placed: 'blue', provisioned: 'green', cancelled: 'red' };
const day = (d: string | null) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '');

function OrdersTab() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<GuOrderStatus | ''>('');
  const [open, setOpen] = useState<GuOrderDto | null>(null);
  const list = useQuery({ queryKey: ['gu-orders', q, status], queryFn: () => get<{ items: GuOrderDto[]; total: number }>(`/gaveuniverset/orders?perPage=100${q ? `&q=${encodeURIComponent(q)}` : ''}${status ? `&status=${status}` : ''}`) });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input className="input w-72" placeholder="Søg ordrenr., shop, firma eller e-mail" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-48" value={status} onChange={(e) => setStatus(e.target.value as GuOrderStatus | '')}>
          <option value="">Alle statusser</option>
          {GU_ORDER_STATUSES.map((s) => <option key={s} value={s}>{GU_ORDER_STATUS_LABELS[s]}</option>)}
        </select>
        <span className="text-sm text-muted">{list.data?.total ?? 0} ordrer</span>
      </div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0 text-sm">
          <thead><tr><th>Ordre</th><th>Bestilt</th><th>Køber</th><th>Gaveshop</th><th className="text-right">Koder</th><th>Åbner / lukker</th><th>Betaling</th><th className="text-right">I alt inkl. moms</th><th>Status</th></tr></thead>
          <tbody>
            {(list.data?.items ?? []).map((o) => (
              <tr key={o.id} className="cursor-pointer hover:bg-canvas" onClick={() => setOpen(o)}>
                <td className="font-medium">{o.number}</td>
                <td className="whitespace-nowrap">{fmtTime(o.placedAt)}</td>
                <td>{o.buyer.companyName}<div className="text-xs text-muted">{o.buyer.email}</div></td>
                <td>{o.shopName}<div className="text-xs text-muted">{o.assortmentName}</div>{o.provisionError && <div className="text-xs text-danger">Gaveshop ikke oprettet</div>}</td>
                <td className="text-right tabular-nums">{o.codes}</td>
                <td className="whitespace-nowrap">{day(o.opensAt)} – {o.continuous ? 'løbende' : day(o.closesAt)}</td>
                <td>{o.paymentMethod === 'card' ? (o.paid ? 'Kort, betalt' : 'Kort') : 'Faktura'}</td>
                <td className="text-right tabular-nums">{kr(o.totalMinor)}</td>
                <td><Badge tone={statusTone[o.status]}>{GU_ORDER_STATUS_LABELS[o.status]}</Badge></td>
              </tr>
            ))}
            {!list.data?.items.length && <tr><td colSpan={9} className="text-muted">Ingen ordrer endnu.</td></tr>}
          </tbody>
        </table>
      )}
      {open && <GuOrderModal o={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function GuOrderModal({ o: initial, onClose }: { o: GuOrderDto; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [o, setO] = useState(initial);
  const [error, setError] = useState<unknown>(null);
  const retry = useMutation({
    mutationFn: () => post<{ shopId: string | null; error: string | null }>(`/gaveuniverset/orders/${o.id}/provision`),
    onSuccess: async (r) => {
      setError(r.error ? new Error(r.error) : null);
      const list = await qc.fetchQuery({ queryKey: ['gu-orders', 'one', o.id], queryFn: () => get<{ items: GuOrderDto[] }>(`/gaveuniverset/orders?q=${o.number}`) });
      const fresh = list.items.find((x) => x.id === o.id);
      if (fresh) setO(fresh);
      void qc.invalidateQueries({ queryKey: ['gu-orders'] });
    },
    onError: setError,
  });
  const facts: [string, string][] = [
    ['Køber', `${o.buyer.companyName} (${o.buyer.email})`],
    ['Faktura til', [o.billing.companyName, `CVR ${o.billing.cvr}`, o.billing.ean ? `EAN ${o.billing.ean}` : '', o.billing.contactName, o.billing.email, o.billing.phone].filter(Boolean).join(', ')],
    ['Sortiment', `${o.assortmentName} (${GU_OCCASION_LABELS[o.occasion]}), ${o.productCount} gaver valgt`],
    ['Gavekoder', `${o.codes} ${GU_CODE_TYPE_LABELS[o.codeType].toLowerCase()}`],
    ['Åbner', day(o.opensAt)],
    ['Lukker', o.continuous ? 'Løbende levering, ingen lukkedato' : day(o.closesAt)],
    ['Levering', `${GU_DELIVERY_LABELS[o.deliveryMethod]}${o.deliveryDate ? `, ugen med ${day(o.deliveryDate)}` : ''}${o.carryingSupport ? ', bæreservice' : ''}`],
    ...o.deliveryAddresses.map((a, i) => [`Adresse ${i + 1}`, [a.company, a.department, a.contactName, a.street, `${a.zip} ${a.city}`, a.phone].filter(Boolean).join(', ')] as [string, string]),
    ['Koderne', o.sendCodesToEmployees ? `Mailes til ${o.employeeCount} medarbejdere ${o.codesSendAt ? `den ${day(o.codesSendAt)}` : 'på åbningsdagen'}` : 'Samlet til køberen'],
    ['Betaling', o.paymentMethod === 'card' ? (o.paid ? 'Kort, godkendt' : 'Kort, ikke betalt') : 'Faktura'],
    ['Gavekoder udstedt', `${o.codesIssued} af ${o.codes}${o.cards ? `, kort nr. ${o.cards.from}-${o.cards.to}` : ''}`],
    ['Faktura', ({ not_due: 'Ikke lavet endnu', due: 'Lavet, venter på godkendelse eller afsendelse', invoiced: 'Sendt', failed: 'Fejlede', none: 'Ingen' } as Record<string, string>)[o.invoiceStatus] ?? o.invoiceStatus],
  ];
  return (
    <Modal title={`${o.number} · ${o.shopName}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <ErrorBox error={error} />
        {o.provisionError && (
          <div className="rounded-lg border border-danger/30 bg-danger/5 p-3">
            <div className="font-semibold text-danger">Gaveshoppen er ikke oprettet</div>
            <div className="mt-1">{o.provisionError}</div>
            {can('gu.manage') && <button className="btn btn-primary mt-2" disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending ? 'Opretter…' : 'Prøv igen'}</button>}
          </div>
        )}
        {o.shop && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas p-3">
            <span className="font-semibold">Gaveshop:</span>
            <Link to="/shops/$id" params={{ id: o.shop.id }} className="text-primary hover:underline">{o.shop.name}</Link>
            <a className="text-primary hover:underline" href={o.shop.url} target="_blank" rel="noreferrer">{o.shop.url}</a>
            <a className="btn ml-auto" href={`/api/gaveuniverset/orders/${o.id}/codes.xlsx`}>Hent gavekoder (XLSX)</a>
          </div>
        )}
        {!o.shop && !o.provisionError && o.status === 'placed' && can('gu.manage') && <button className="btn" disabled={retry.isPending} onClick={() => retry.mutate()}>Opret gaveshoppen nu</button>}
        <dl className="grid grid-cols-[9rem_1fr] gap-y-1">{facts.map(([k, v]) => <div key={k} className="contents"><dt className="text-muted">{k}</dt><dd>{v}</dd></div>)}</dl>
        <table className="table text-sm">
          <thead><tr><th>Linje</th><th className="text-right">Antal</th><th className="text-right">Stk.</th><th className="text-right">I alt ekskl. moms</th></tr></thead>
          <tbody>
            {o.lines.map((l) => <tr key={l.key}><td>{l.label}</td><td className="text-right tabular-nums">{l.quantity}</td><td className="text-right tabular-nums">{kr(l.unitMinor)}</td><td className="text-right tabular-nums">{kr(l.totalMinor)}</td></tr>)}
            <tr><td colSpan={3} className="text-right text-muted">Moms</td><td className="text-right tabular-nums">{kr(o.vatMinor)}</td></tr>
            <tr><td colSpan={3} className="text-right font-semibold">I alt inkl. moms</td><td className="text-right font-semibold tabular-nums">{kr(o.totalMinor)}</td></tr>
          </tbody>
        </table>
        <div className="flex justify-between">
          <Link to="/orders/$id" params={{ id: o.orderId }} className="btn">Åbn ordren</Link>
          <button className="btn" onClick={onClose}>Luk</button>
        </div>
      </div>
    </Modal>
  );
}

// --- physical cards (P3.4, GU-07) --------------------------------------------------------------------

function CardsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [batch, setBatch] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState<GuCardImportResultDto | null>(null);
  const pool = useQuery({ queryKey: ['gu-cards'], queryFn: () => get<GuCardPoolDto>('/gaveuniverset/cards') });
  const [limit, setLimit] = useState<number | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const imp = useMutation({
    mutationFn: () => post<GuCardImportResultDto>('/gaveuniverset/cards/import', { batch, text }),
    onSuccess: (r) => { setResult(r); setError(null); if (!r.errors.length) { setText(''); void qc.invalidateQueries({ queryKey: ['gu-cards'] }); } },
    onError: setError,
  });
  const saveAlert = useMutation({
    mutationFn: () => put<Settings>('/settings', { 'gu.cardLowStock': limit ?? pool.data?.lowStock ?? 200, 'gu.cardAlertEmail': email ?? pool.data?.alertEmail ?? '' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gu-cards'] }); setError(null); },
    onError: setError,
  });
  const p = pool.data;
  const lines = text.split(/\r?\n/).filter((l) => l.trim()).length;
  return (
    <div className="space-y-4">
      <ErrorBox error={error} />
      {!p ? <Spinner /> : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className={`card ${p.free < p.lowStock ? 'border-danger' : ''}`}><div className="label">Ledige kort</div><div className="text-2xl font-semibold tabular-nums">{p.free}</div>{p.free < p.lowStock && <div className="text-xs text-danger">Under grænsen på {p.lowStock}</div>}</div>
            <div className="card"><div className="label">Reserveret til ordrer</div><div className="text-2xl font-semibold tabular-nums">{p.reserved}</div></div>
            <div className="card"><div className="label">Kasseret</div><div className="text-2xl font-semibold tabular-nums">{p.void}</div></div>
          </div>
          <table className="table card p-0 text-sm">
            <thead><tr><th>Parti</th><th className="text-right">Kort</th><th className="text-right">Ledige</th><th>Numre</th><th>Læst ind</th></tr></thead>
            <tbody>
              {p.batches.map((b) => <tr key={b.batch ?? '-'}><td>{b.batch ?? '–'}</td><td className="text-right tabular-nums">{b.count}</td><td className="text-right tabular-nums">{b.free}</td><td className="tabular-nums">{b.fromSeq}–{b.toSeq}</td><td>{fmtTime(b.importedAt)}</td></tr>)}
              {!p.batches.length && <tr><td colSpan={5} className="text-muted">Ingen kort endnu. Læs trykkeriets fil ind herunder.</td></tr>}
            </tbody>
          </table>
          {can('settings.manage') && (
            <div className="card flex flex-wrap items-end gap-3 text-sm">
              <label className="block"><span className="label">Advar når der er færre ledige end</span><input className="input w-32" type="number" min={0} value={limit ?? p.lowStock} onChange={(e) => setLimit(Math.max(0, Number(e.target.value) || 0))} /></label>
              <label className="block flex-1"><span className="label">Advarsel til (e-mail)</span><input className="input" value={email ?? p.alertEmail} placeholder="gaveteam@borgbigum.dk" onChange={(e) => setEmail(e.target.value)} /></label>
              <button className="btn" disabled={saveAlert.isPending} onClick={() => saveAlert.mutate()}>Gem</button>
            </div>
          )}
        </>
      )}
      {can('gu.manage') && (
        <div className="card space-y-3 text-sm">
          <div className="font-semibold">Læs gavekort ind</div>
          <p className="text-muted">Indsæt trykkeriets fil: ét kort pr. linje, kortnummer og kode adskilt af semikolon, tabulator eller komma (fx <span className="font-mono">100001;K7M4PX9Q2R</span>). Står der kun koder, nummereres de videre fra det højeste nummer i puljen. En ordre får altid kort med numre i træk.</p>
          <label className="block w-64"><span className="label">Parti</span><input className="input" value={batch} placeholder="fx Jul 2026, levering 1" onChange={(e) => setBatch(e.target.value)} /></label>
          <textarea className="input min-h-40 font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} placeholder={'Kortnummer;Kode\n100001;K7M4PX9Q2R\n100002;H3TQ8WZ5NM'} />
          <div className="flex items-center gap-3">
            <button className="btn btn-primary" disabled={!batch.trim() || !lines || imp.isPending} onClick={() => imp.mutate()}>{imp.isPending ? 'Læser ind…' : `Læs ${lines} linjer ind`}</button>
            {result && !result.errors.length && <span className="text-success">{result.imported} kort læst ind{result.skipped ? `, ${result.skipped} fandtes i forvejen` : ''}.</span>}
          </div>
          {result?.errors.length ? <ul className="list-disc pl-5 text-danger">{result.errors.map((e) => <li key={e}>{e}</li>)}</ul> : null}
        </div>
      )}
    </div>
  );
}

// --- shops (P3.5, GU-14) ---------------------------------------------------------------------------

const Tick = ({ on, label }: { on: boolean; label: string }) => <span title={label} className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${on ? 'bg-success-100 text-success' : 'bg-canvas text-muted'}`}>{on ? '✓' : '–'}</span>;
const stateLabel = { upcoming: 'Åbner snart', open: 'Åben', closed: 'Lukket' } as const;

function ShopsTable({ rows, onOpen }: { rows: GuStaffShopDto[]; onOpen: (id: string) => void }) {
  return (
    <table className="table card p-0 text-sm">
      <thead><tr><th>Gaveshop</th><th>Køber</th><th>Status</th><th className="text-right">Koder</th><th className="text-right">Valgt</th><th className="text-center">Pakket</th><th className="text-center">Sendt</th><th className="text-center">Faktureret</th><th className="text-center">Betalt</th><th className="text-right">Ordrer i alt</th></tr></thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.id} className="cursor-pointer hover:bg-canvas" onClick={() => onOpen(s.id)}>
            <td className="font-medium">{s.name}<div className="text-xs text-muted">{s.assortmentName}</div></td>
            <td>{s.buyer.companyName}</td>
            <td className="whitespace-nowrap">{stateLabel[s.state]}<div className="text-xs text-muted">{day(s.opensAt)} – {s.continuous ? 'løbende' : day(s.closesAt)}</div></td>
            <td className="text-right tabular-nums">{s.codes}</td>
            <td className="text-right tabular-nums">{s.chosen}<div className="text-xs text-muted">{s.codes ? Math.round((s.chosen / s.codes) * 100) : 0} %</div></td>
            <td className="text-center"><Tick on={s.giftOrders > 0 && s.packed === s.giftOrders} label={`${s.packed} af ${s.giftOrders} gaveordrer pakket`} /><div className="text-xs text-muted">{s.packed}/{s.giftOrders}</div></td>
            <td className="text-center"><Tick on={s.giftOrders > 0 && s.shipped === s.giftOrders} label={`${s.shipped} af ${s.giftOrders} gaveordrer sendt`} /><div className="text-xs text-muted">{s.shipped}/{s.giftOrders}</div></td>
            <td className="text-center"><Tick on={s.invoiced} label="Alle købers ordrer er faktureret" /></td>
            <td className="text-center"><Tick on={s.paid} label="Alle købers ordrer er betalt" /></td>
            <td className="text-right tabular-nums">{kr(s.totalMinor)}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={10} className="text-muted">Ingen gaveshops endnu.</td></tr>}
      </tbody>
    </table>
  );
}

function ShopsTab() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['gu-shops', q], queryFn: () => get<GuStaffShopDto[]>(`/gaveuniverset/shops${q ? `?q=${encodeURIComponent(q)}` : ''}`) });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input className="input w-72" placeholder="Søg shop, firma eller e-mail" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="text-sm text-muted">{list.data?.length ?? 0} gaveshops</span>
      </div>
      {list.isLoading ? <Spinner /> : <ShopsTable rows={list.data ?? []} onOpen={setOpen} />}
      {open && <GuShopModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function GuShopModal({ id, onClose }: { id: string; onClose: () => void }) {
  const d = useQuery({ queryKey: ['gu-shop', id], queryFn: () => get<GuShopDetailDto>(`/gaveuniverset/shops/${id}`) });
  const s = d.data;
  return (
    <Modal title={s ? s.name : 'Gaveshop'} onClose={onClose}>
      {!s ? <Spinner /> : (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/shops/$id" params={{ id: s.id }} className="btn">Shoppen i admin</Link>
            <a className="btn" href={s.url} target="_blank" rel="noreferrer">Åbn webshoppen</a>
            <a className="btn" href={`/api/gaveuniverset/shops/${s.id}/choices.xlsx`}>Gavevalg (XLSX)</a>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="card"><div className="label">Koder</div><div className="text-xl font-semibold">{s.codes}</div></div>
            <div className="card"><div className="label">Valgt</div><div className="text-xl font-semibold">{s.chosen}</div></div>
            <div className="card"><div className="label">Givet tilbage</div><div className="text-xl font-semibold">{s.adjust.returned}</div></div>
            <div className="card"><div className="label">Status</div><div className="text-xl font-semibold">{stateLabel[s.state]}</div></div>
          </div>
          <div>
            <div className="label">Købers ordrer</div>
            <table className="table text-sm">
              <tbody>
                {s.orders.map((o) => (
                  <tr key={o.id}><td className="font-medium">{o.number}</td><td>{fmtTime(o.placedAt)}</td><td className="text-right">{o.codes} koder</td><td className="text-right">{kr(o.totalMinor)}</td><td>{GU_ORDER_STATUS_LABELS[o.status]}</td><td className="text-right"><a className="text-primary hover:underline" href={`/api/gaveuniverset/orders/${o.id}/pdf`}>PDF</a> · <Link to="/orders/$id" params={{ id: o.orderId }} className="text-primary hover:underline">Ordre</Link></td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="label">Gavevalg ({s.chosen} af {s.codes})</div>
            <div className="max-h-72 overflow-auto">
              <table className="table text-xs">
                <thead><tr><th>Medarbejder</th><th>Afdeling</th><th>Kode</th><th>Gave</th></tr></thead>
                <tbody>{s.choices.map((c) => <tr key={c.code}><td>{c.name ?? ''}<div className="text-muted">{c.email ?? ''}</div></td><td>{c.department ?? ''}</td><td className="font-mono">{c.code}</td><td>{c.gift ?? <span className="text-muted">ikke valgt</span>}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end"><button className="btn" onClick={onClose}>Luk</button></div>
        </div>
      )}
    </Modal>
  );
}

/** A buyer's shops and orders, under the buyer form. */
function BuyerShops({ buyerId }: { buyerId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['gu-shops', 'buyer', buyerId], queryFn: () => get<GuStaffShopDto[]>(`/gaveuniverset/shops?buyerId=${buyerId}`) });
  if (!list.data?.length) return null;
  return (
    <div>
      <div className="label">Gaveshops</div>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {list.data.map((s) => <li key={s.id}><button type="button" className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-canvas" onClick={() => setOpen(s.id)}><span className="flex-1 font-medium">{s.name}</span><span className="text-muted">{s.chosen}/{s.codes} valgt</span><span className="tabular-nums">{kr(s.totalMinor)}</span></button></li>)}
      </ul>
      {open && <GuShopModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// --- closing and stock (P3.6, GU-15, REP-04) ---------------------------------------------------------

const eventLabel: Record<GuClosingEventDto['kind'], string> = { reminder_7: 'Påmindelse, 7 dage før', reminder_3: 'Påmindelse, 3 dage før', close: 'Lukning: standardgaver og lukkemail' };

function ClosingTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<GuClosingRunDto | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const events = useQuery({ queryKey: ['gu-closing'], queryFn: () => get<GuClosingEventDto[]>('/gaveuniverset/closing/events') });
  const stock = useQuery({ queryKey: ['gu-stock'], queryFn: () => get<GuStockRowDto[]>('/gaveuniverset/stock-report') });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings'), enabled: can('settings.manage') });
  const run = useMutation({
    mutationFn: () => post<GuClosingRunDto>('/gaveuniverset/closing/run'),
    onSuccess: (r) => { setResult(r); setError(null); void qc.invalidateQueries({ queryKey: ['gu-closing'] }); void qc.invalidateQueries({ queryKey: ['gu-stock'] }); void qc.invalidateQueries({ queryKey: ['gu-shops'] }); },
    onError: setError,
  });
  const saveEmail = useMutation({ mutationFn: () => put<Settings>('/settings', { 'gu.reportEmail': email ?? '' }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['settings'] }); setError(null); }, onError: setError });
  const short = (stock.data ?? []).filter((r) => r.shortfall > 0);
  return (
    <div className="space-y-4">
      <ErrorBox error={error} />
      <div className="card space-y-3 text-sm">
        <p className="text-muted">Hver morgen fra kl. 7 sender systemet påmindelser 7 og 3 dage før en gaveshop lukker (til køberen med gavevalgene og til medarbejdere med ubrugte koder), og dagen efter lukning laver det en ordre med standardgaven til hver ubrugt kode og sender køberen den endelige liste. Hvert trin sker kun én gang, så det er sikkert at køre igen.</p>
        <div className="flex flex-wrap items-end gap-3">
          {can('gu.manage') && <button className="btn btn-primary" disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? 'Kører…' : 'Kør nu'}</button>}
          {result && <span>{result.reminders} påmindelser til købere, {result.employeeReminders} til medarbejdere, {result.closed} shops lukket med {result.autoGifts} standardgaver{result.withoutDefault ? `, ${result.withoutDefault} uden standardgave` : ''}{result.report ? ', dagsrapport sendt' : ''}.</span>}
          {can('settings.manage') && settings.data && (
            <div className="ml-auto flex items-end gap-2">
              <label className="block"><span className="label">Dagsrapport til (e-mail)</span><input className="input w-72" value={email ?? settings.data['gu.reportEmail']} placeholder="gaveteam@borgbigum.dk" onChange={(e) => setEmail(e.target.value)} /></label>
              <button className="btn" disabled={saveEmail.isPending || email == null} onClick={() => saveEmail.mutate()}>Gem</button>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="label">Kommende og manglende trin</div>
        {events.isLoading ? <Spinner /> : (
          <table className="table card p-0 text-sm">
            <thead><tr><th>Dato</th><th>Trin</th><th>Gaveshop</th><th>Køber</th><th className="text-right">Valgt</th><th>Standardgave</th><th>Status</th></tr></thead>
            <tbody>
              {(events.data ?? []).map((e) => (
                <tr key={`${e.shopId}-${e.kind}`}>
                  <td className="whitespace-nowrap">{day(e.on)}</td>
                  <td>{eventLabel[e.kind]}</td>
                  <td>{e.shopName}<div className="text-xs text-muted">lukker {day(e.closesAt)}</div></td>
                  <td>{e.buyer}</td>
                  <td className="text-right tabular-nums">{e.chosen}/{e.codes}</td>
                  <td>{e.hasDefaultGift ? 'Ja' : <span className="text-danger">Mangler</span>}</td>
                  <td>{e.done ? <Badge tone="green">{e.kind === 'close' ? 'Gjort' : 'Sendt'}</Badge> : e.kind !== 'close' && e.on < new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' }) && e.closesAt <= new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' }) ? <Badge tone="amber">Ikke sendt</Badge> : <Badge tone="zinc">Venter</Badge>}</td>
                </tr>
              ))}
              {!events.data?.length && <tr><td colSpan={7} className="text-muted">Ingen gaveshops lukker de næste tre uger.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <div className="label">Lagerrapport: valgte gaver, der venter på lageret, mod hvad der kan sælges ({short.length} mangler)</div>
        {stock.isLoading ? <Spinner /> : (
          <table className="table card p-0 text-sm">
            <thead><tr><th>Varenr.</th><th>Vare</th><th className="text-right">Shops</th><th className="text-right">Venter</th><th className="text-right">Heraf standardgaver</th><th className="text-right">Til salg</th><th className="text-right">Mangler</th></tr></thead>
            <tbody>
              {(stock.data ?? []).map((r) => (
                <tr key={r.variantId} className={r.shortfall > 0 ? 'bg-danger/5' : ''}>
                  <td className="font-mono text-xs"><Link to="/stock/$variantId" params={{ variantId: r.variantId }} className="text-primary hover:underline">{r.sku}</Link></td>
                  <td>{r.title}</td>
                  <td className="text-right tabular-nums">{r.shops}</td>
                  <td className="text-right tabular-nums">{r.waiting}</td>
                  <td className="text-right tabular-nums">{r.autoGifts}</td>
                  <td className="text-right tabular-nums">{r.sellable}</td>
                  <td className={`text-right tabular-nums ${r.shortfall > 0 ? 'font-semibold text-danger' : ''}`}>{r.shortfall}</td>
                </tr>
              ))}
              {!stock.data?.length && <tr><td colSpan={7} className="text-muted">Ingen gaver venter.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
